'use client';

import { ErrorInfo, useCallback, useMemo } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { useTranslations } from 'next-intl';
import useLocalStorage from '@/hooks/useLocalStorage';
import { useImageBaseUrl } from '@/atoms/processEnvAtom';
import { ProductCardProps } from '@/components/ProductCard/ProductCard';
import RecentlyViewed from '@/components/RecentlyViewed/RecentlyViewed';
import { RECENTLY_ITEM_KEY } from '@/components/RecentlyViewed/RecentlyViewedTracker';
import { RecentlyViewedSellable } from '@/components/RecentlyViewed/recentlyViewedTypes';
import { isAppearanceImage, toAppearanceImageFromAppearance } from '@/utility/appearanceConfig';
import { removeLocalStorageItem } from '@/utility/localStorage';
import { trackError } from '@/utility/raygun';
import { tracker } from '@/utility/tracking/tracker';
import { getSellableDetailHrefArgs } from '@/utility/urls';

const STORAGE_KEY = `${RECENTLY_ITEM_KEY}-sellable`;

export type RecentlyViewedSellablesProps = {
  className?: string;
};

const RecentlyViewedSellables = (props: RecentlyViewedSellablesProps) => {
  const handleError = useCallback((error: unknown, info: ErrorInfo) => {
    removeLocalStorageItem(STORAGE_KEY);
    trackError(error as Error, info);
  }, []);

  return (
    // Avoid total failure of the page if something goes wrong in this component
    <ErrorBoundary onError={handleError} fallback={null}>
      <InternalRecentlyViewedSellables {...props} />
    </ErrorBoundary>
  );
};

const InternalRecentlyViewedSellables = ({ className }: RecentlyViewedSellablesProps) => {
  const t = useTranslations('recentlyViewed');
  const imageBaseUrl = useImageBaseUrl();
  const [recentlyViewedSellables, storeRecentlyViewedItems] = useLocalStorage<
    RecentlyViewedSellable[]
  >(STORAGE_KEY, []);

  const clearHistory = useCallback(() => storeRecentlyViewedItems([]), [storeRecentlyViewedItems]);

  const items = useMemo<ProductCardProps[]>(() => {
    return recentlyViewedSellables.map((sellable, index) => ({
      background: 'gray',
      title: sellable.compositionName,
      subTitle: sellable.productTypeName,
      productTypeId: sellable.productTypeId,
      price: sellable.price,
      provider: t('byDesigner', { designer: sellable.userName }),
      link: {
        href: getSellableDetailHrefArgs({
          compositionId: sellable.compositionId,
          sellableName: sellable.compositionName,
          productTypeName: sellable.productTypeName,
          sellableId: sellable.sellableId,
          appearanceId: sellable.appearanceId,
        }),
        onClick: (_, appearanceId: string) => {
          tracker.trackFeature({
            featureUsed: 'product-card-click',
            detail: 'reco recently-viewed',
            product: {
              productTypeId: sellable.productTypeId,
              appearanceId,
              price: sellable.price,
              amount: 1,
              articleId: sellable.compositionId,
              articleName: sellable.compositionName,
              sellableId: sellable.sellableId,
              position: index + 1,
            },
          });
        },
      },
      imageBaseUrl,
      images: [
        {
          type: 'product',
          alt: sellable.compositionName,
          vpString: sellable.vpString,
          viewId: sellable.viewId,
          modelId: sellable.modelImageId,
          appearanceId: sellable.appearanceId,
          crop: sellable.crop,
          loading: 'lazy',
          priority: 'low',
          title: `${sellable.compositionName}-${sellable.productTypeName}`,
        },
      ],
      appearances: sellable.appearances.map((appearance) =>
        isAppearanceImage(appearance) ? appearance : toAppearanceImageFromAppearance(appearance)
      ),
      defaultAppearanceId: sellable.appearanceId,
      favorite: {
        label: sellable.compositionName,
        sellableId: sellable.sellableId,
        compositionId: sellable.compositionId,
        productTypeId: sellable.productTypeId,
        appearanceId: sellable.appearanceId,
        userId: sellable.userId,
        shopId: sellable.shopId,
        price: sellable.price,
        partnerName: sellable.userName,
      },
    }));
  }, [imageBaseUrl, recentlyViewedSellables, t]);

  if (!items.length) {
    return null;
  }

  return <RecentlyViewed className={className} items={items} clearHistory={clearHistory} />;
};

export default RecentlyViewedSellables;
